import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import * as api from './api';
import EventForm from './EventForm';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const formatDateRange = (startDate, endDate) => {
    if (!startDate) return '—';
    const s = new Date(startDate + 'T00:00:00');
    const month = MONTHS[s.getMonth()];
    const dayStart = s.getDate();
    if (!endDate || endDate === startDate) return `${month} ${dayStart}`;
    const e = new Date(endDate + 'T00:00:00');
    if (s.getMonth() === e.getMonth()) {
        return `${month} ${dayStart}–${e.getDate()}`;
    }
    return `${month} ${dayStart} – ${MONTHS[e.getMonth()]} ${e.getDate()}`;
};

const calcEventDays = (ev) => {
    if (!ev.startDate || !ev.endDate) return '?';
    const start = new Date(ev.startDate + 'T00:00:00');
    const end = new Date(ev.endDate + 'T00:00:00');
    return Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)));
};

const isPastEvent = (ev) => {
    const d = ev.endDate || ev.startDate;
    if (!d) return false;
    return new Date(d + 'T23:59:59') < new Date();
};

const getYear = (ev) => {
    const d = ev.startDate || ev.endDate;
    if (!d) return '????';
    return new Date(d + 'T00:00:00').getFullYear();
};

const EventList = ({ token, onManageAttendance }) => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [selectedEventId, setSelectedEventId] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

    const loadEvents = async () => {
        try {
            setLoading(true);
            const data = await api.fetchEvents();
            setEvents(data);
        } catch {
            toast.error('Failed to load events');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadEvents(); }, [token]);

    const eventsByYear = useMemo(() => {
        const groups = {};
        for (const ev of events) {
            const y = getYear(ev);
            if (!groups[y]) groups[y] = [];
            groups[y].push(ev);
        }
        // Years descending, events within each year ascending by date
        return Object.keys(groups)
            .sort((a, b) => Number(b) - Number(a))
            .map(year => ({
                year,
                events: groups[year].sort((a, b) => {
                    const da = a.startDate || a.endDate || '';
                    const db = b.startDate || b.endDate || '';
                    return da.localeCompare(db);
                })
            }));
    }, [events]);

    const handleCreate = () => { setEditingEvent(null); setShowForm(true); };
    const handleEdit = (ev) => { setEditingEvent(ev); setShowForm(true); };
    const handleFormCancel = () => { setShowForm(false); setEditingEvent(null); };

    const handleFormSave = async (data) => {
        try {
            if (editingEvent) {
                await api.updateEvent(editingEvent.eventId, data);
                toast.success('Event updated');
            } else {
                await api.createEvent(data);
                toast.success('Event created');
            }
            setShowForm(false);
            setEditingEvent(null);
            await loadEvents();
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Failed to save event');
        }
    };

    const handleDeleteConfirm = async () => {
        if (!showDeleteConfirm) return;
        try {
            await api.deleteEvent(showDeleteConfirm.eventId);
            toast.success('Event deleted');
            setShowDeleteConfirm(null);
            setSelectedEventId(null);
            await loadEvents();
        } catch {
            toast.error('Failed to delete event');
        }
    };

    if (loading) return <div className="em-loading">Loading events…</div>;

    return (
        <div className="em-event-list">
            <div className="em-toolbar">
                <button className="em-btn em-btn-primary" onClick={handleCreate}>+ New Event</button>
            </div>

            {showForm && (
                <div className="em-modal-overlay" onClick={handleFormCancel}>
                    <div className="em-modal" onClick={e => e.stopPropagation()}>
                        <EventForm event={editingEvent} onSave={handleFormSave} onCancel={handleFormCancel} />
                    </div>
                </div>
            )}

            {showDeleteConfirm && (
                <div className="em-modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
                    <div className="em-modal em-confirm-modal" onClick={e => e.stopPropagation()}>
                        <h3 className="em-confirm-title">Delete Event?</h3>
                        <p className="em-confirm-text">
                            Permanently delete <strong>{showDeleteConfirm.site}</strong> ({formatDateRange(showDeleteConfirm.startDate, showDeleteConfirm.endDate)}) and all attendance data?
                        </p>
                        <div className="em-form-actions">
                            <button className="em-btn em-btn-danger" onClick={handleDeleteConfirm}>Delete Event</button>
                            <button className="em-btn em-btn-secondary" onClick={() => setShowDeleteConfirm(null)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {events.length === 0 ? (
                <div className="em-empty">No events yet. Create one to get started.</div>
            ) : (
                eventsByYear.map(({ year, events: yearEvents }) => (
                    <div key={year} className="em-year-group">
                        <div className="em-year-header">{year}</div>
                        <table className="em-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Days</th>
                                    <th>Site</th>
                                    <th>Type</th>
                                    <th className="em-center">Attendees</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                {yearEvents.map(ev => {
                                    const past = isPastEvent(ev);
                                    const selected = selectedEventId === ev.eventId;
                                    return (
                                        <React.Fragment key={ev.eventId}>
                                            <tr
                                                className={`em-event-row ${past ? 'em-past' : 'em-future'} ${selected ? 'em-selected' : ''}`}
                                                onClick={() => setSelectedEventId(prev => prev === ev.eventId ? null : ev.eventId)}
                                            >
                                                <td className="em-date-cell">{formatDateRange(ev.startDate, ev.endDate)}</td>
                                                <td className="em-center">{calcEventDays(ev)}</td>
                                                <td>{ev.site}</td>
                                                <td>{ev.eventType}</td>
                                                <td className="em-center">{ev.attendeeCount ?? '—'}</td>
                                                <td className="em-desc-cell">{ev.description || '—'}</td>
                                            </tr>
                                            {selected && (
                                                <tr className="em-action-row">
                                                    <td colSpan={6}>
                                                        <div className="em-action-bar">
                                                            <button className="em-btn em-btn-small em-btn-accent" onClick={e => { e.stopPropagation(); onManageAttendance(ev); }}>Manage Attendance</button>
                                                            <button className="em-btn em-btn-small" onClick={e => { e.stopPropagation(); handleEdit(ev); }}>Edit Event</button>
                                                            <button className="em-btn em-btn-small em-btn-danger" onClick={e => { e.stopPropagation(); setShowDeleteConfirm(ev); }}>Delete Event</button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ))
            )}
        </div>
    );
};

export default EventList;
